import { FLOW_NAMES } from "./flows";

/**
 * Buffer what a flow did, and post it later (#379).
 *
 * ── WHY THE CLIENT IS THE WRITER ──
 *
 * #379, directly: "`Started` and `Abandoned` are client-side concepts --
 * the server never learns that somebody opened a screen and left. That
 * means either a client event endpoint, or accepting that only
 * `Completed` is ever recorded, which quietly makes the abandonment
 * column meaningless rather than empty."
 *
 * The second option was rejected on its own terms. "Nobody abandons
 * anything" is the most flattering wrong answer the table could give,
 * and a column that is always zero reads as a finding.
 *
 * ── IT BUFFERS, BECAUSE THE ALTERNATIVE IS A WRITE PER NAVIGATION ──
 *
 * Also #379: "A per-screen-open event is a write per navigation, and on
 * mobile that is a write per navigation on a metered connection. It must
 * batch, and it must survive being offline."
 *
 * So events accumulate in memory, are mirrored to storage so a closed
 * tab does not lose them, and are posted in one call on a timer or when
 * the page goes away. Nothing here is urgent: the whole point of the
 * table is to answer questions weeks later.
 *
 * ── THE BUFFER OVERFLOWS BY DROPPING THE OLDEST ──
 *
 * A keeper offline for a week accumulates events with nowhere to go, and
 * an unbounded buffer is an unbounded localStorage write on a device
 * that has other uses for it. At the cap the OLDEST go, which is the
 * opposite of the usual choice and deliberate: the newest events are the
 * ones whose flow may still be in progress, and dropping a Started while
 * keeping its Completed produces a completion the funnel cannot explain.
 *
 * ── EVERY FAILURE HERE IS SILENT, ON PURPOSE ──
 *
 * These events are a by-product of work somebody already did and does
 * not know is being measured. A toast saying "could not record
 * analytics" would be the app complaining about its own bookkeeping to
 * the person least able to act on it -- and worse, it would read as a
 * failure of the thing they just finished. So nothing here notifies,
 * nothing throws into a caller, and a failed flush keeps its events for
 * the next one.
 *
 * MIRRORS mobile/src/services/flowTelemetry.js, registered as a parity
 * pair. The STORAGE differs by platform (localStorage here, MMKV there)
 * and the lifecycle hook differs (pagehide here, AppState there); every
 * rule about what is recorded and what is dropped is the same, because
 * two clients measuring differently produce one funnel that means
 * neither.
 */

const STORAGE_KEY = "flow.telemetry.buffer";
/* 200 events. Roughly a fortnight of heavy use for one keeper, and small
   enough that the JSON stays trivial to write on every change. */
const MAX_BUFFERED = 200;
/* The server takes 100 at a time (FlowEventBatch), so a full buffer
   flushes in two calls rather than being refused as one. */
const MAX_PER_FLUSH = 100;
const FLUSH_INTERVAL_MS = 60_000;

/* Flows that have started and not yet ended, by name -> start time.
   IN MEMORY ONLY, and that is the correct scope: a flow interrupted by
   the tab closing is ABANDONED, and the pagehide handler says so. Held
   across a reload it would instead look like a flow somebody spent
   three days on. */
const open = new Map();

let buffer = restore();
let timer = null;
let flushing = false;

/*
 * WHO ACTUALLY SENDS IT, injected rather than imported.
 *
 * useFlow imports this module, so every screen that measures a flow
 * inherits whatever this file depends on. Importing the API client here
 * put NetInfo -- a native module -- into the dependency graph of every
 * instrumented screen, which under jest surfaced as those screens
 * failing on a native binding: a test-environment problem reported as an
 * application failure.
 *
 * A dynamic import() was the first fix and was a worse one. It works
 * under Metro and THROWS under jest ("a dynamic import callback was
 * invoked without --experimental-vm-modules"), straight into the catch
 * below that exists to keep analytics quiet -- so the transport was
 * broken in tests and the tests said nothing. A silent failure inside
 * the mechanism whose whole job is to fail silently is the one place
 * laziness cannot be bought back later.
 *
 * So the app wires the transport in at startup and this file knows
 * nothing about HTTP. It also puts the "are we online" question where
 * the answer lives, which on mobile is the point: a request that cannot
 * succeed is a timeout and a metered byte or two for nothing.
 *
 * Until it is set, events still accumulate. That is deliberate --
 * onboarding starts before much of the app exists, and it is the flow
 * whose abandonment matters most.
 */
let poster = null;

export function setFlowPoster(fn) {
  poster = fn;
}

function restore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // A private window, cleared site data, or a half-written value. An
    // empty buffer is always a safe answer here -- the cost is losing
    // events nobody is waiting for.
    return [];
  }
}

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(buffer));
  } catch {
    // Quota, or storage disabled. The in-memory buffer still flushes;
    // only surviving a reload is lost.
  }
}

function eventId() {
  /* Unique per event, because it is the DEDUPE KEY. The buffer retries,
     so a batch whose response was lost arrives twice, and on a funnel
     that inflates Started without inflating Completed -- an abandonment
     rate that drifts upward with signal strength. V122 holds this under
     a unique index. Same shape as the offline queue's
     client_action_id. */
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function record(name, status, durationSeconds) {
  /* A name outside the registry is DROPPED rather than sent. A typo does
     not error -- it quietly becomes an extra flow with one event in it,
     and the admin surface then reports a flow nobody built. */
  if (!FLOW_NAMES.includes(name)) return;

  buffer.push({
    client_event_id: eventId(),
    feature_name: name,
    status,
    /* The DEVICE's clock. The event happened when it happened, not when
       the connection came back -- a batch posted next morning would
       otherwise record an evening's work as one minute of it. */
    occurred_at: new Date().toISOString(),
    ...(durationSeconds == null ? {} : { duration_seconds: durationSeconds }),
  });

  // OLDEST first out. See the header: dropping a Started while keeping
  // its Completed produces a completion the funnel cannot explain.
  if (buffer.length > MAX_BUFFERED) buffer = buffer.slice(-MAX_BUFFERED);
  persist();
}

/** A flow somebody has begun. Re-starting an open flow restarts its clock. */
export function startFlow(name) {
  if (!FLOW_NAMES.includes(name)) return;
  open.set(name, Date.now());
  record(name, "Started");
}

function endFlow(name, status) {
  const startedAt = open.get(name);
  /* No start means no duration, and the event still goes -- a Completed
     whose Started was lost (a reload, a buffer trim) is a worse record
     than one with a null duration, because the funnel would show a
     completion with nothing leading to it either way and this at least
     counts it. duration_seconds is nullable for exactly this. */
  open.delete(name);
  record(name, status,
    startedAt == null ? undefined : Math.round((Date.now() - startedAt) / 1000));
}

/** They finished it. */
export function completeFlow(name) {
  endFlow(name, "Completed");
}

/** They left without finishing. */
export function abandonFlow(name) {
  endFlow(name, "Abandoned");
}

/**
 * Everything still open is abandoned.
 *
 * Called when the page goes away, which is the commonest abandonment
 * there is and the one no component can report for itself: a keeper who
 * closes the tab mid-inspection never runs another line of our code.
 */
export function abandonOpenFlows() {
  for (const name of [...open.keys()]) abandonFlow(name);
}

/** Post what is buffered. Safe to call at any time; never throws. */
export async function flush() {
  if (flushing || buffer.length === 0) return;
  if (!poster) return;

  flushing = true;
  try {
    const batch = buffer.slice(0, MAX_PER_FLUSH);
    const r = await poster(batch);
    /* Drop what the server ACCEPTED, which includes the duplicates it
       already held -- keeping those would retry them forever. Matched by
       id rather than by trimming a count, because another event may have
       been recorded while the request was in flight. */
    const sent = new Set(batch.map((e) => e.client_event_id));
    const accepted = r?.accepted ?? 0;
    if (accepted > 0) {
      buffer = buffer.filter((e) => !sent.has(e.client_event_id));
      persist();
    }
  } catch {
    // Keep everything and try later. Offline is the expected case, not
    // an error -- this is the half of #379 that says the offline rule
    // applies to a new write path.
  } finally {
    flushing = false;
  }
}

/**
 * Start the timer and the page-away hook. Idempotent.
 *
 * pagehide rather than beforeunload: beforeunload does not fire reliably
 * on mobile Safari, where a tab is backgrounded rather than closed, and
 * that is the platform most likely to drop a keeper mid-flow.
 */
export function startFlowTelemetry() {
  if (timer) return () => {};
  timer = setInterval(flush, FLUSH_INTERVAL_MS);

  const onHide = () => {
    abandonOpenFlows();
    /* A synchronous-ish best effort. There is no await to be had here,
       so what survives is the PERSISTED buffer, which the next session
       flushes. That is the whole reason the buffer is mirrored to
       storage rather than kept in memory. */
    flush();
  };
  window.addEventListener("pagehide", onHide);

  return () => {
    clearInterval(timer);
    timer = null;
    window.removeEventListener("pagehide", onHide);
  };
}

/* Test seam. Nothing in the app calls this -- the module is a singleton
   on purpose, because two buffers would each flush half the events and
   neither would know about the other's open flows. */
export function __resetForTests() {
  poster = null;
  buffer = [];
  open.clear();
  flushing = false;
  if (timer) clearInterval(timer);
  timer = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}
